import { ROTransaction, RWTransaction, runWithROTransaction } from '../../../db'
import {
  Id,
  enumGetValues,
  validateOptionalId,
  validateRequiredEmail,
  validateRequiredString,
  Context,
  HashingService,
  throwObjectAlreadyExists,
  throwObjectNotFound,
  validateRequiredId,
  PaginatedResult,
  BaseDBServiceOpts,
  CoreDict,
  BaseDBService,
  validateOptionalPhone,
  runIfDefined,
  throwInvalidParameter,
  throwAccessDenied,
  normalizeAnswer,
  throwInvalidOperation,
  trCore,
  createObj,
  getUpdatedString,
} from '../../../common'
import { CreateEventMode, ModelEvent, ModelEventCreator, ModelEventsProcessor } from '../../events'
import { MunicipalEntitiesRepository } from '../../municipalEntity'
import { Permissions } from '../../authorization'

import { RoleType, RolesRepository } from './roles'
import { AddUserOpts, UpdateUserOpts, User, UserFindOptions, UsersRepository, UsersService, UserStatus } from './users'
import { ControlQuestionsRepository } from './controlQuestions'
import { CardIssuePointsRepository } from '../../cardIssuePoints'
import { FullTextSearchService } from '../../fullTextSearch'
import { isEqual } from 'lodash'

enum EventType {
  USER_ADD = 'USER_ADD',
  USER_UPDATE = 'USER_UPDATE',
  USER_DELETE = 'USER_DELETE',
  USER_MERGE = 'USER_MERGE',
}

type AddUserPayload = {
  email: string
  passwordHash: string
  surname: string
  name: string
  patronymic?: string
  phone?: string
  municipalEntityId?: Id
  roleId: Id
  status: UserStatus
  controlQuestion?: string
  controlAnswerHash?: string
  cardIssuePointId?: Id
}

type UpdateUserPayload = {
  passwordHash?: string
  surname?: string
  name?: string
  patronymic?: string
  phone?: string
  municipalEntityId?: Id
  roleId?: Id
  status?: UserStatus
  controlQuestion?: string
  controlAnswerHash?: string
  cardIssuePointId?: Id | null
}

interface DBUsersServiceOpts extends BaseDBServiceOpts<CoreDict> {
  usersRepository: UsersRepository
  rolesRepository: RolesRepository
  hashingService: HashingService
  municipalEntitiesRepository: MunicipalEntitiesRepository
  controlQuestionsRepository: ControlQuestionsRepository
  cardIssuePointsRepository: CardIssuePointsRepository
}

export class DBUsersService extends BaseDBService<DBUsersServiceOpts, CoreDict> implements UsersService {
  constructor(opts: DBUsersServiceOpts) {
    super('DBUsersService', opts)
    this.logger.debug({}, '<2125190b> Initialized')
  }
  private validateUserID = (userID?: string) => validateRequiredId('<87edf38f> User ID', userID)
  private validateEmail = (email: string) => validateRequiredEmail('<1ef0db9c> Email', email.trim())
  private validatePassword = (password: string) => validateRequiredString('<4b83b384> Password', password.trim())
  private validateName = (name: string) => validateRequiredString('<b689f977> Name', name.trim(), { minSize: 2 })
  private validateSurname = (surname: string) =>
    validateRequiredString('<395aab3f> Surname', surname.trim(), { minSize: 2 })
  private validatePhone = (phone: string) => validateOptionalPhone('<51f06244> Phone', phone.trim())
  private async validateMunicipalEntity(ctx: Context, tx: ROTransaction, municipalEntityId: Id): Promise<Id> {
    validateRequiredId('<8925dc7f> Municipal entity ID', municipalEntityId)
    ;(await this.opts.municipalEntitiesRepository.findByIdNonDeleted(tx, municipalEntityId)) ??
      throwObjectNotFound(
        `<df2ebadd> ${this.translate(ctx, d => d.users.municipalEntityNotFoundError, municipalEntityId)}`,
      )
    return municipalEntityId
  }

  private async checkForUserExists(ctx: Context, tx: ROTransaction, id: Id): Promise<User> {
    return (
      (await this.opts.usersRepository.findByIdNonDeleted(tx, id)) ??
      throwObjectNotFound(`<39c9cb27> ${this.translate(ctx, d => d.users.notFoundByIdError, id)}`)
    )
  }

  private async validateRole(ctx: Context, tx: ROTransaction, roleId: Id): Promise<Id> {
    validateRequiredId('<951fded9> Role ID', roleId)
    const newRole =
      (await this.opts.rolesRepository.findById(tx, roleId)) ??
      throwObjectNotFound(`<5755ecc8> ${this.translate(ctx, d => d.roles.notFoundByIdError, roleId)}`)

    // allowed for internal requests
    if (ctx.internal) return roleId

    const currentUser = await runIfDefined(ctx.userId, it => this.opts.usersRepository.findById(tx, it))
    const currentUserRole = await runIfDefined(currentUser?.roleId, it => this.opts.rolesRepository.findById(tx, it))
    if (!currentUserRole) {
      throwInvalidParameter(`<70929af7> ${this.translate(ctx, d => d.users.createWithSpecifiedRoleNotAllowedError)}`)
    }
    switch (currentUserRole.type) {
      case RoleType.ANONYM:
      case RoleType.OPERATOR:
        throwInvalidParameter(`<774bd487> ${this.translate(ctx, d => d.users.createWithSpecifiedRoleNotAllowedError)}`)
        break
      case RoleType.MODERATOR:
        if (newRole.type !== RoleType.OPERATOR) {
          throwInvalidParameter(
            `<dbd31925> ${this.translate(ctx, d => d.users.createWithSpecifiedRoleNotAllowedError)}`,
          )
        }
        break
      case RoleType.ADMIN:
        // allowed to create with any role
        break
    }
    return roleId
  }

  private validateControlAnswer = (answer: string) =>
    validateRequiredString('<3415ae00> Password', normalizeAnswer(answer))

  private async validateControlQuestion(tx: ROTransaction, id: Id): Promise<Id> {
    validateOptionalId('<29b2e502> Control question ID', id)
    ;(await this.opts.controlQuestionsRepository.find(tx, id)) ??
      throwInvalidParameter('<9727104f> Invalid control question id')
    return id
  }

  private async validateCardIssuePoint(ctx: Context, tx: ROTransaction, cardIssuePointId?: Id | null) {
    validateOptionalId('<681489bc> Card issue point ID', cardIssuePointId)
    if (cardIssuePointId) {
      ;(await this.opts.cardIssuePointsRepository.findByIdNonDeleted(tx, cardIssuePointId)) ??
        throwObjectNotFound(
          `<90155318> ${this.translate(ctx, d => d.cardIssuePoints.notFoundByIdError, cardIssuePointId)}`,
        )
    }
    return cardIssuePointId
  }

  private async checkAccessToMunicipalEntity(
    errorCode: string,
    ctx: Context,
    tx: ROTransaction,
    meIds: (string | undefined)[],
    userID?: Id,
  ): Promise<void> {
    const filteredMEIds = meIds?.filter(id => id != undefined)

    userID ||= this.validateUserID(ctx.userId)

    const user = await this.checkForUserExists(ctx, tx, userID)

    filteredMEIds.forEach(id => {
      if (user.municipalEntityId != id) {
        throwAccessDenied(
          `${errorCode} ${this.opts.translationService.translate(ctx.langCode, d => d.municipalEntities.accessDenied)}`,
        )
      }
    })
  }

  async add(ctx: Context, opts: AddUserOpts): Promise<Id> {
    this.logger.debug(ctx, '<0eff4a14> Add user', { opts })

    await runWithROTransaction(this.opts.dbConnection, async tx => {
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_MANAGE)
        if (!opts.municipalEntityId)
          // Модератор не может создать администратора
          throwAccessDenied(
            this.translate(ctx, d => d.municipalEntities.accessDenied),
            { uid: '<b1bd2946>' },
          )

        await this.checkAccessToMunicipalEntity('<a37897c4>', ctx, tx, [opts.municipalEntityId])
      }
    })

    const email = this.validateEmail(opts.email)

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      ;(await this.opts.usersRepository.findByEmail(tx, email)) &&
        throwObjectAlreadyExists(`<1669a489> ${this.translate(ctx, d => d.users.alreadyExistsError, opts.email)}`)
      await this.validateCardIssuePoint(ctx, tx, opts.cardIssuePointId)

      const entityId = opts.id ?? this.opts.idGenerator.generate()
      const municipalEntityId = await runIfDefined(opts.municipalEntityId, v =>
        this.validateMunicipalEntity(ctx, tx, v),
      )

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.NEW,
        entityId,
        type: EventType.USER_ADD,
        municipalEntityId,
        payload: createObj<AddUserPayload>({
          email,
          passwordHash: this.opts.hashingService.hash(this.validatePassword(opts.password)),
          name: this.validateName(opts.name),
          surname: this.validateSurname(opts.surname),
          patronymic: opts.patronymic,
          phone: runIfDefined(opts.phone, v => this.validatePhone(v)),
          municipalEntityId,
          roleId: await this.validateRole(ctx, tx, opts.roleId),
          status: opts.status,
          controlQuestion: await runIfDefined(opts.controlQuestionId, v => this.validateControlQuestion(tx, v)),
          controlAnswerHash: runIfDefined(opts.controlAnswer, v =>
            this.opts.hashingService.hash(this.validateControlAnswer(v)),
          ),
          cardIssuePointId: opts.cardIssuePointId,
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])

    return event.entityId
  }

  async findByIdNonDeleted(ctx: Context, id: string): Promise<User | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const user = await this.opts.usersRepository.findById(tx, id)
      if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_VIEW))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_VIEW)

        if (!user) return
        await this.checkAccessToMunicipalEntity('<029a3b8e>', ctx, tx, [user?.municipalEntityId])
      }

      return user?.deletedAt ? undefined : user
    })
  }

  async update(ctx: Context, id: Id, opts: UpdateUserOpts): Promise<void> {
    this.logger.debug(ctx, '<035c64da> Update user', { id, opts })

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const user = await this.opts.usersRepository.findById(tx, id)

      if (!user || user.deletedAt)
        throwObjectNotFound(`<87727127> ${this.translate(ctx, d => d.users.notFoundByIdError, id)}`)

      // check for self update
      if (ctx.userId === id && (await this.opts.permissionsService.hasPermission(ctx, Permissions.PROFILE_MANAGE))) {
        if (opts.roleId && opts.roleId !== user.roleId) {
          // cannot change self role
          throwAccessDenied(`<4bc698c5> ${this.translate(ctx, d => d.users.roleChangeNotAllowedError)}`)
        }
      } else {
        if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_MANAGE))) {
          await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_MANAGE)

          if (!user?.municipalEntityId)
            // Модератор не может изменять администратора
            throwAccessDenied(
              this.translate(ctx, d => d.municipalEntities.accessDenied),
              { uid: '<ca91a55d>' },
            )

          await this.checkAccessToMunicipalEntity('<5edb25b0>', ctx, tx, [
            opts?.municipalEntityId,
            user?.municipalEntityId,
          ])
        }
      }

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        type: EventType.USER_UPDATE,
        municipalEntityId: user.municipalEntityId,
        payload: createObj<UpdateUserPayload>({
          passwordHash: runIfDefined(opts.password, v => this.opts.hashingService.hash(this.validatePassword(v))),
          name: runIfDefined(opts.name, v => this.validateName(v)),
          surname: runIfDefined(opts.surname, v => this.validateSurname(v)),
          patronymic: opts.patronymic,
          phone: opts.phone ? this.validatePhone(opts.phone) : opts.phone,
          municipalEntityId: await runIfDefined(opts.municipalEntityId, v => this.validateMunicipalEntity(ctx, tx, v)),
          roleId: await runIfDefined(opts.roleId, v => this.validateRole(ctx, tx, v)),
          status: opts.status,
          controlQuestion: await runIfDefined(opts.controlQuestionId, v => this.validateControlQuestion(tx, v)),
          controlAnswerHash: runIfDefined(opts.controlAnswer, v =>
            this.opts.hashingService.hash(this.validateControlAnswer(v)),
          ),
          cardIssuePointId: await runIfDefined(opts.cardIssuePointId, v => this.validateCardIssuePoint(ctx, tx, v)),
        }),
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async delete(ctx: Context, id: Id): Promise<void> {
    this.logger.debug(ctx, '<2cf548c9> Delete user', { id })

    const event = await runWithROTransaction(this.opts.dbConnection, async tx => {
      const currentUser = await this.checkForUserExists(ctx, tx, this.validateUserID(ctx.userId))
      const user = await this.opts.usersRepository.findById(tx, id)
      if (currentUser.id === user?.id) throwInvalidOperation(trCore(d => d.users.selfDeletionNotAllowedError))
      else if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_MANAGE))) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_MANAGE)

        if (!user?.municipalEntityId)
          // Модератор не может изменять администратора
          throwAccessDenied(
            this.translate(ctx, d => d.municipalEntities.accessDenied),
            { uid: '<559eafdb>' },
          )

        await this.checkAccessToMunicipalEntity('<7ee163fd>', ctx, tx, [user?.municipalEntityId])
      }
      if (!user || user.deletedAt)
        throwObjectNotFound(`<5d216dc7> ${this.translate(ctx, d => d.users.notFoundByIdError, id)}`)

      return await this.opts.eventCreator.create(ctx, tx, {
        mode: CreateEventMode.UPDATE,
        entityId: id,
        municipalEntityId: user.municipalEntityId,
        type: EventType.USER_DELETE,
      })
    })

    await this.opts.eventDispatcher.processEvents(ctx, [event])
  }

  async findAll(ctx: Context, opts?: UserFindOptions): Promise<PaginatedResult<User>> {
    await runWithROTransaction(this.opts.dbConnection, async tx => {
      const ids = opts?.filter?.ids
      const municipalEntityId = opts?.filter?.municipalEntityId
      const user = await runIfDefined(ctx.userId, userId => {
        return this.opts.usersRepository.findById(tx, userId)
      })

      if (
        ids &&
        ids.length === 1 &&
        ids[0] === ctx.userId &&
        (await this.opts.permissionsService.hasPermission(ctx, Permissions.PROFILE_VIEW))
      ) {
        // allowed to view self profile
      } else if (
        !(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_VIEW)) &&
        user?.municipalEntityId === municipalEntityId
      ) {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_VIEW)
      } else {
        await this.opts.permissionsService.checkPermission(ctx, Permissions.USERS_VIEW)
      }
    })

    return await runWithROTransaction(this.opts.dbConnection, tx => this.opts.usersRepository.findAll(tx, opts))
  }

  async findById(ctx: Context, id: string): Promise<User | undefined> {
    return await runWithROTransaction(this.opts.dbConnection, async tx => {
      const user = await this.opts.usersRepository.findById(tx, id)
      if (ctx.userId === id && (await this.opts.permissionsService.hasPermission(ctx, Permissions.PROFILE_VIEW))) {
        // allowed to view self profile
      } else {
        if (!(await this.opts.permissionsService.hasPermission(ctx, Permissions.USERS_VIEW))) {
          await this.opts.permissionsService.checkPermission(ctx, Permissions.ME_USERS_VIEW)

          if (!user) return

          await this.checkAccessToMunicipalEntity('<2916e26d>', ctx, tx, [user?.municipalEntityId])
        }
      }
      return user
    })
  }

  async findByEmail(ctx: Context, email: string): Promise<User | undefined> {
    await this.opts.permissionsService.checkPermission(ctx, Permissions.USERS_VIEW)
    return await runWithROTransaction(this.opts.dbConnection, tx => this.opts.usersRepository.findByEmail(tx, email))
  }
}

function getFieldsToIndex(u: User): (string | undefined)[] {
  return [u.name, u.surname, u.patronymic, u.phone, u.email]
}

export class UsersModelEventProcessor implements ModelEventsProcessor {
  supportedEventTypes: string[] = enumGetValues(EventType)

  constructor(
    private opts: {
      usersRepository: UsersRepository
      eventCreator: ModelEventCreator
      fullTextSearchService: FullTextSearchService
    },
  ) {}

  async createMergeEvent(ctx: Context, tx: ROTransaction, entityId: string): Promise<ModelEvent> {
    const u = await this.opts.usersRepository.findById(tx, entityId)
    return await this.opts.eventCreator.create(ctx, tx, {
      mode: CreateEventMode.UPDATE,
      entityId: entityId,
      type: EventType.USER_MERGE,
      payload: {
        ...u,
        deletedAt: undefined,
        createdAt: undefined,
      },
    })
  }

  async process(ctx: Context, tx: RWTransaction, event: ModelEvent): Promise<boolean> {
    switch (event.type) {
      case EventType.USER_ADD: {
        const p = event.payload as AddUserPayload
        const user: User = {
          id: event.entityId,
          createdAt: event.createdAt,
          email: p.email,
          passwordHash: p.passwordHash,
          name: p.name,
          surname: p.surname,
          patronymic: p.patronymic,
          phone: p.phone,
          municipalEntityId: p.municipalEntityId,
          roleId: p.roleId,
          status: p.status,
          controlQuestionId: p.controlQuestion,
          controlAnswerHash: p.controlAnswerHash,
          cardIssuePointId: p.cardIssuePointId,
        }
        await this.opts.usersRepository.save(tx, user)
        await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, getFieldsToIndex(user))
        return true
      }
      case EventType.USER_MERGE:
      case EventType.USER_UPDATE: {
        const p = event.payload as UpdateUserPayload
        const user =
          (await this.opts.usersRepository.findById(tx, event.entityId)) ??
          throwObjectNotFound(`<e7ec811d> User not found by id=${event.entityId}`)

        const updatedUser: User = {
          ...user,
          passwordHash: p.passwordHash ?? user.passwordHash,
          name: p.name ?? user.name,
          surname: p.surname ?? user.surname,
          patronymic: p.patronymic ?? user.patronymic,
          phone: getUpdatedString(p.phone, user.phone),
          municipalEntityId: p.municipalEntityId ?? user.municipalEntityId,
          roleId: p.roleId ?? user.roleId,
          status: p.status ?? user.status,
          controlQuestionId: p.controlQuestion ?? user.controlQuestionId,
          controlAnswerHash: p.controlAnswerHash ?? user.controlAnswerHash,
          cardIssuePointId: p.cardIssuePointId ?? user.cardIssuePointId,
        }

        await this.opts.usersRepository.save(tx, updatedUser)

        const fieldsToIndex = getFieldsToIndex(updatedUser)
        if (!isEqual(getFieldsToIndex(user), fieldsToIndex)) {
          await this.opts.fullTextSearchService.addToIndex(ctx, tx, event.entityId, fieldsToIndex)
        }

        return true
      }
      case EventType.USER_DELETE: {
        await this.opts.usersRepository.delete(tx, event.entityId, event.createdAt)
        await this.opts.fullTextSearchService.removeFromIndex(ctx, tx, event.entityId)
        return true
      }
    }
    return false
  }
}
